// apps/web/src/lib/economy.ts
// ─────────────────────────────────────────────────────────────────────────
// CLIENT-SIDE GEM ECONOMY (transitional)
//
// This is the V1 implementation: localStorage-backed, optimistic, no server
// validation. It uses the SAME EARN_REWARDS table as the future server impl,
// so the values can never disagree between client and server.
//
// There is NO daily gem cap: organic earning is uncapped by design (product
// decision: the rare completionist gets fully rewarded). The idempotency
// ledger (rewardLedger) plus the sync rate limits are the anti-farm rails.
//
// When server-side validation lands:
// - awardGems() becomes "queue operation + apply optimistically + sync"
// - the rest of the API stays identical
// - components don't change
// ─────────────────────────────────────────────────────────────────────────

import {
	EARN_REWARDS,
	STREAK_MILESTONES,
	streakBonusFor,
	type EarnEventType,
	type SpendEventType,
	type GemBalance,
	type GemTransaction,
	emptyGemBalance,
} from '@nisk/core/economy';
import { streakDayISO } from '@nisk/core/srs';

// ── Storage keys ─────────────────────────────────────────────────────────
const BALANCE_KEY = 'nis.economy.balance';
const TRANSACTIONS_KEY = 'nis.economy.transactions';
const DAILY_LOGIN_KEY = 'nis.economy.dailyLogin'; // last UTC date
const LOCAL_USER_ID = 'local-user'; // until auth wires this up

const MAX_TRANSACTIONS_KEPT = 50; // recent ones only — full audit moves to server later

// ── Internal storage helpers ─────────────────────────────────────────────
function readBalance(): GemBalance {
	try {
		const raw = localStorage.getItem(BALANCE_KEY);
		if (raw) return JSON.parse(raw) as GemBalance;
	} catch {
		/* noop */
	}
	return emptyGemBalance(LOCAL_USER_ID);
}

function writeBalance(b: GemBalance): void {
	try {
		localStorage.setItem(BALANCE_KEY, JSON.stringify(b));
	} catch {
		/* noop */
	}
}

function readTransactions(): GemTransaction[] {
	try {
		const raw = localStorage.getItem(TRANSACTIONS_KEY);
		if (raw) return JSON.parse(raw) as GemTransaction[];
	} catch {
		/* noop */
	}
	return [];
}

function writeTransactions(txns: GemTransaction[]): void {
	const trimmed = txns.slice(-MAX_TRANSACTIONS_KEPT);
	try {
		localStorage.setItem(TRANSACTIONS_KEY, JSON.stringify(trimmed));
	} catch {
		/* noop */
	}
}

// The product-wide learning day (a new day starts at 5:00 PM UTC), so the
// daily login reward flips at the same moment as streaks and Daily Review.
function learningDayKey(): string {
	return streakDayISO();
}

function uuid(): string {
	return `txn-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

// ── Public API ───────────────────────────────────────────────────────────

export function getBalance(): GemBalance {
	return readBalance();
}

export function getRecentTransactions(limit = 10): GemTransaction[] {
	const txns = readTransactions();
	return txns.slice(-limit).reverse();
}

/**
 * Award gems for an event. Returns the actual amount credited
 * (zero if the event type carries no reward).
 *
 * Uncapped: there is no daily limit on organic earning. Dedupe/abuse
 * protection lives in the reward ledger and the sync rate limits.
 */
export function awardGems(
	eventType: EarnEventType,
	entityId: string | undefined,
	options: { metadata?: Record<string, unknown>; streakDays?: number } = {},
): { credited: number; balance: GemBalance } {
	// Compute base amount
	let amount = EARN_REWARDS[eventType];
	if (eventType === 'streak_milestone' && options.streakDays !== undefined) {
		amount = streakBonusFor(options.streakDays);
	}

	if (amount <= 0) {
		return { credited: 0, balance: readBalance() };
	}

	// Apply to balance
	const balance = readBalance();
	const next: GemBalance = {
		...balance,
		total: balance.total + amount,
		lifetimeEarned: balance.lifetimeEarned + amount,
		updatedAt: Date.now(),
	};
	writeBalance(next);

	// Append transaction
	const txn: GemTransaction = {
		id: uuid(),
		userId: LOCAL_USER_ID,
		direction: 'earn',
		amount,
		eventType,
		entityId,
		metadata: options.metadata,
		createdAt: Date.now(),
		status: 'confirmed', // V1 = client-confirmed; server will downgrade to 'pending' until validated
	};
	writeTransactions([...readTransactions(), txn]);

	return { credited: amount, balance: next };
}

/** Spend gems on a cosmetic item. Fails (returns false) if insufficient balance. */
export function spendGems(
	eventType: SpendEventType,
	amount: number,
	entityId?: string,
	metadata?: Record<string, unknown>,
): { ok: boolean; balance: GemBalance; reason?: string } {
	if (amount <= 0)
		return { ok: false, balance: readBalance(), reason: 'invalid_amount' };

	const balance = readBalance();
	if (balance.total < amount) {
		return { ok: false, balance, reason: 'insufficient_balance' };
	}

	const next: GemBalance = {
		...balance,
		total: balance.total - amount,
		lifetimeSpent: balance.lifetimeSpent + amount,
		updatedAt: Date.now(),
	};
	writeBalance(next);

	const txn: GemTransaction = {
		id: uuid(),
		userId: LOCAL_USER_ID,
		direction: 'spend',
		amount,
		eventType,
		entityId,
		metadata,
		createdAt: Date.now(),
		status: 'confirmed',
	};
	writeTransactions([...readTransactions(), txn]);

	return { ok: true, balance: next };
}

// ── Daily-login helper ───────────────────────────────────────────────────
/**
 * Claim daily login reward. Returns the credited amount (0 if already claimed today).
 * Call this on app boot or when user first interacts.
 */
export function claimDailyLogin(): {
	credited: number;
	alreadyClaimed: boolean;
	balance: GemBalance;
} {
	const lastClaim = localStorage.getItem(DAILY_LOGIN_KEY);
	const today = learningDayKey();
	if (lastClaim === today) {
		return { credited: 0, alreadyClaimed: true, balance: readBalance() };
	}

	try {
		localStorage.setItem(DAILY_LOGIN_KEY, today);
	} catch {
		/* noop */
	}
	const { credited, balance } = awardGems('daily_login', today);
	return { credited, alreadyClaimed: false, balance };
}

/**
 * Award a streak milestone bonus. Caller passes the day count.
 * Returns 0 if no milestone matches that day count.
 */
export function awardStreakMilestone(streakDays: number): {
	credited: number;
	balance: GemBalance;
} {
	const bonus = streakBonusFor(streakDays);
	if (bonus <= 0) return { credited: 0, balance: readBalance() };
	const { credited, balance } = awardGems(
		'streak_milestone',
		`streak-${streakDays}`,
		{ streakDays },
	);
	return { credited, balance };
}

/**
 * Reconcile the local gem balance to a server-authoritative total.
 *
 * Used by multi-device sync: when operations pulled from OTHER devices include
 * any economy.* op, the per-op deltas can't be replayed locally without drift
 * (the earn path would re-record a new op). Instead we fetch the server's
 * authoritative gem total once and snap `balance.total` to it — the server is
 * the source of truth across devices.
 *
 * This is intentionally a STATE-ONLY setter: it does not touch the transaction
 * log or fire any reward/toast. lifetimeEarned / lifetimeSpent stay as-is
 * (those are local-history fields the server doesn't return today via the
 * balance endpoint); only `total` and `updatedAt` move. Returns the updated
 * balance so callers can push it straight into Redux.
 */
export function reconcileBalanceFromServer(serverGems: number): GemBalance {
	const current = readBalance();
	// Guard against a malformed/negative server value snapping the UI to junk.
	const safeTotal =
		Number.isFinite(serverGems) && serverGems >= 0
			? Math.floor(serverGems)
			: current.total;

	if (safeTotal === current.total) return current;

	const next: GemBalance = {
		...current,
		total: safeTotal,
		updatedAt: Date.now(),
	};
	writeBalance(next);
	return next;
}

// ── Re-exports for convenience ───────────────────────────────────────────
export { EARN_REWARDS, STREAK_MILESTONES };

export type { EarnEventType, SpendEventType, GemBalance, GemTransaction };
